/**
 * JWT Token Management Service
 *
 * Wave S1: Security Hardening - Frontend JWT Integration
 *
 * Handles JWT token storage, refresh, and expiration checking.
 * Works with the backend JWT endpoints:
 * - POST /api/v1/auth/token/ - Obtain tokens
 * - POST /api/v1/auth/token/refresh/ - Refresh access token
 * - POST /api/v1/auth/token/verify/ - Verify token
 */

import { authClient, isAxiosError } from './authClient';
import { logger } from '../utils/logger';
import {
  decodeJwtPayload,
  getTokenExpiry,
  isTokenExpired,
  REFRESH_BUFFER_MS,
  MIN_REFRESH_INTERVAL_MS,
  JWT_STORAGE_KEYS,
} from '../../../shared/services/jwtService';

// Re-export core utilities for consumers that only need decode/expiry helpers
export { decodeJwtPayload, getTokenExpiry, isTokenExpired };

// Storage keys (aliased from shared constants for local readability)
const ACCESS_TOKEN_KEY = JWT_STORAGE_KEYS.ACCESS_TOKEN;
const REFRESH_TOKEN_KEY = JWT_STORAGE_KEYS.REFRESH_TOKEN;
const TOKEN_EXPIRY_KEY = JWT_STORAGE_KEYS.TOKEN_EXPIRY;
const LEGACY_TOKEN_KEY = JWT_STORAGE_KEYS.LEGACY_TOKEN;

// Global refresh promise to prevent concurrent refresh attempts
let refreshPromise: Promise<string | null> | null = null;
let lastRefreshAttempt = 0;

/**
 * Store JWT tokens
 */
export function storeTokens(accessToken: string, refreshToken: string): void {
  localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
  localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);

  // Store expiry for quick checks without decoding
  const expiry = getTokenExpiry(accessToken);
  if (expiry) {
    localStorage.setItem(TOKEN_EXPIRY_KEY, expiry.toString());
  }

  // Remove legacy token if present
  localStorage.removeItem(LEGACY_TOKEN_KEY);
}

/**
 * Get access token (may be expired — callers should use getAuthHeader() for requests)
 *
 * Returns the stored access token regardless of expiry.  Callers that need
 * to verify freshness should call needsRefresh() separately.
 */
export function getAccessToken(): string | null {
  const accessToken = localStorage.getItem(ACCESS_TOKEN_KEY);
  if (accessToken) {
    return accessToken;
  }

  // Fall back to legacy token for backward compatibility
  const legacyToken = localStorage.getItem(LEGACY_TOKEN_KEY);
  return legacyToken;
}

/**
 * Get refresh token
 */
export function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
}

/**
 * Check if using JWT (vs legacy token)
 */
export function isUsingJwt(): boolean {
  return !!localStorage.getItem(ACCESS_TOKEN_KEY);
}

/**
 * Check if access token needs refresh.
 *
 * Returns true when:
 *  - Access token is present and within REFRESH_BUFFER_MS of expiry, OR
 *  - Access token is absent but a refresh token IS present (allows the
 *    request interceptor's preflight refresh to obtain a new access token
 *    instead of sending the request unauthenticated and getting a 403).
 */
export function needsRefresh(): boolean {
  const accessToken = localStorage.getItem(ACCESS_TOKEN_KEY);

  if (!accessToken) {
    // No access token — only trigger refresh if a refresh token exists.
    // Without this, the preflight refresh is skipped and the request goes out
    // without an Authorization header → backend returns 403 instead of 401.
    return Boolean(localStorage.getItem(REFRESH_TOKEN_KEY));
  }

  return isTokenExpired(accessToken, REFRESH_BUFFER_MS);
}

/**
 * Refresh the access token using the refresh token
 * Returns the new access token or null if refresh failed
 */
export async function refreshAccessToken(): Promise<string | null> {
  // Prevent multiple simultaneous refresh attempts
  if (refreshPromise) {
    return refreshPromise;
  }

  // Rate limit refresh attempts
  const now = Date.now();
  if (now - lastRefreshAttempt < MIN_REFRESH_INTERVAL_MS) {
    logger.debug('[JWT] Skipping refresh - too soon since last attempt');
    return getAccessToken();
  }

  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    logger.debug('[JWT] No refresh token available');
    return null;
  }

  // Check if refresh token itself is expired
  if (isTokenExpired(refreshToken)) {
    logger.debug('[JWT] Refresh token expired');
    clearTokens();
    return null;
  }

  lastRefreshAttempt = now;

  refreshPromise = (async () => {
    try {
      logger.debug('[JWT] Refreshing access token...');

      // Use dedicated auth client (no interceptors) to avoid refresh loops in apiService.ts
      const response = await authClient.post(
        '/auth/token/refresh/',
        { refresh: refreshToken },
        { timeout: 5000 }
      );

      const { access, refresh: newRefresh } = response.data;

      // Store new tokens (refresh token rotates)
      storeTokens(access, newRefresh || refreshToken);

      logger.debug('[JWT] Token refreshed successfully');
      return access;
    } catch (error) {
      logger.error('[JWT] Token refresh failed:', error);

      // If refresh fails with 401, tokens are invalid
      if (isAxiosError(error) && error.response?.status === 401) {
        clearTokens();
      }

      return null;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

/**
 * Clear all tokens (logout)
 */
export function clearTokens(): void {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(TOKEN_EXPIRY_KEY);
  localStorage.removeItem(LEGACY_TOKEN_KEY);
}

/**
 * Get Authorization header value
 * Returns 'Bearer <token>' for JWT or 'Token <token>' for legacy
 *
 * IMPORTANT: We ALWAYS attach the token even if it's expired.  Sending an
 * expired token lets the backend respond with 401, which triggers the
 * response interceptor's refresh-and-retry flow.  If we return null here
 * the request goes out as anonymous → backend resolves no tenant →
 * ViewSets return empty results (200 + []) → React Query caches the
 * empty data → every page shows "No data" — a silent, catastrophic
 * failure mode that cannot self-heal.
 */
export function getAuthHeader(): string | null {
  const accessToken = localStorage.getItem(ACCESS_TOKEN_KEY);
  if (accessToken) {
    return `Bearer ${accessToken}`;
  }

  // Fall back to legacy token
  const legacyToken = localStorage.getItem(LEGACY_TOKEN_KEY);
  if (legacyToken) {
    return `Token ${legacyToken}`;
  }

  // No token available (expected during login)
  return null;
}

/**
 * Migrate from legacy token to JWT
 * Called after successful JWT login to clean up
 */
export function migrateLegacyToken(): void {
  localStorage.removeItem(LEGACY_TOKEN_KEY);
}

/**
 * Get token claims (decoded payload)
 */
export function getTokenClaims(): { [key: string]: unknown } | null {
  const accessToken = localStorage.getItem(ACCESS_TOKEN_KEY);
  if (!accessToken) return null;
  return decodeJwtPayload(accessToken);
}

/**
 * Get tenant info from token claims
 */
export function getTenantFromToken(): {
  tenantIds?: string[];
  defaultTenantId?: string;
  defaultTenantSlug?: string;
  defaultTenantRole?: string;
} | null {
  const claims = getTokenClaims();
  if (!claims) return null;
  return {
    tenantIds: claims.tenant_ids as string[] | undefined,
    defaultTenantId: claims.default_tenant_id as string | undefined,
    defaultTenantSlug: claims.default_tenant_slug as string | undefined,
    defaultTenantRole: claims.default_tenant_role as string | undefined,
  };
}
